import { Suspense, useEffect } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
	useChildMatches,
} from '@tanstack/react-router';

import {
	Box,
	Container,
	LinearProgress,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';

import { riskQueries } from '@repo/api/risk/risks';

import { Route as RiskDataRoute } from '~pages/_risks/risk.$riskId/_risk/data';
import { Route as AnalysisRoute } from './calculations/analysis';
import { Route as SummaryRoute } from './calculations/summary';
import { Route as DocumentsRoute } from './documents';

export const Route = createFileRoute('/_risks/risk/$riskId/_risk/calculations')(
	{
		beforeLoad: ({ params, context }) => {
			if (!context.user?.permissions.includes('risk.calculation.view')) {
				throw redirect({
					to: RiskDataRoute.to,
					params,
				});
			}
		},
		notFoundComponent: () => <Typography>Not found</Typography>,
		component: function CalculationsPage() {
			const navigate = Route.useNavigate();

			const matchedId = useChildMatches({
				select: (match) => match[0]?.routeId,
			});

			useEffect(() => {
				if (!matchedId) {
					void navigate({
						to: SummaryRoute.to,
						replace: true,
					});
				}
			}, [matchedId, navigate]);

			const { riskId } = Route.useParams();
			const { data: risk } = useSuspenseQuery(riskQueries.id(riskId));

			useEffect(() => {
				if (
					risk.riskDocuments.length === 0 ||
					!risk.riskDocuments.every(
						(document) => document.documentStatus === 'Completed',
					)
				) {
					void navigate({
						to: DocumentsRoute.to,
					});
				}
			}, [risk.riskDocuments, navigate]);

			return (
				<Container
					sx={{ flexGrow: 1, mb: 2, display: 'flex', flexDirection: 'column' }}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flexGrow: 1,
							backgroundColor: 'offwhite.light',
							p: 1,
						}}
					>
						<Tabs
							value={matchedId}
							variant="scrollable"
							sx={{
								minHeight: '32px',
								'& .MuiTabs-flexContainer': {
									gap: 0.5,
								},
								'& .MuiTab-root': {
									backgroundColor: '#dddddd',
									color: '#424242',
									paddingBlock: 1,
									fontSize: '0.9rem',
									fontWeight: '500',
									textWrap: 'balance',
									flex: 1,
								},
								'& .Mui-selected': {
									color: '#424242',
									backgroundColor: 'white',
								},
							}}
							slotProps={{
								indicator: {
									sx: {
										transform: 'scaleX(0.7)',
										backgroundColor: 'accent.main',
										height: '3px',
									},
								},
							}}
						>
							<Tab
								value={SummaryRoute.id}
								component={Link}
								to={SummaryRoute.to}
								replace
								label="Projected Annual BI Value Summary"
							/>
							<Tab
								value={AnalysisRoute.id}
								component={Link}
								to={AnalysisRoute.to}
								replace
								label="Detailed Analysis"
							/>
						</Tabs>
						<Box
							sx={{
								flexGrow: 1,
								p: 2,
								backgroundColor: 'white',
							}}
						>
							<Suspense fallback={<LinearProgress />}>
								<Outlet />
							</Suspense>
						</Box>
					</Box>
				</Container>
			);
		},
		head: (ctx) => ctx.match.context.setTitle('Risk', 'BI Analysis'),
	},
);
